/**
 * Interaction Catalog UI
 * Issue #314: Browsable catalog of all detected user interactions
 * Issue #351: Updated terminology - "interactions" for user actions, "E2E flows" for the journey
 *
 * Features:
 * - List all user interactions detected in the codebase
 * - Search by name, component, or handler
 * - Filter by trigger type
 * - Group by component, file, or screen
 * - Click to view interaction's E2E flow or source
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type { E2EFlowPath, FlowTriggerType, E2EFlowStep, FlowStepType, InteractionCategory } from '@veodiagram/shared';
import { getStepTypeColors, STEP_TYPE_LABELS } from './E2EFlowNodes';
import { ConfidenceBadge } from '../../../shared/ConfidenceBadge';
import {
  useResolvedNames,
  useAINaming,
  useNameFeedback,
  useAIStatus,
  type NamingContext,
  type NamingResult,
  type ResolvedName,
} from '../../../../hooks/useInteractionNaming';
import { AIContextPreviewModal } from '../../../interactions/AIContextPreviewModal';
import { api } from '../../../../services/api';

// Issue #437: Enhancement summary type
interface EnhancementSummary {
  hasEnhancements: boolean;
  applied: boolean;
  missedCount: number;
  suggestedCount: number;
  totalCount: number;
  latestEnhancementDate: string;
}

// ============= Types =============

interface InteractionCatalogProps {
  flows: E2EFlowPath[];
  onSelectFlow?: (flow: E2EFlowPath) => void;
  onSelectStep?: (step: E2EFlowStep, flow: E2EFlowPath) => void;
  className?: string;
  projectId?: string;  // Issue #362: Required for AI naming features
  onNameUpdated?: (flowId: string, newName: string, source: 'override' | 'ai' | 'pattern') => void;
  refreshTrigger?: number;  // Increment to trigger name refresh (e.g., after bulk naming)
}

// Issue #362: Name source badge colors
// Issue #385: Added 'deep-ai' for deep analysis naming (using orange for better contrast)
const NAME_SOURCE_COLORS: Record<'override' | 'ai' | 'deep-ai' | 'pattern', { bg: string; text: string; border: string; label: string; icon: string }> = {
  'override': { bg: '#dcfce7', text: '#166534', border: '#22c55e', label: 'Custom', icon: '✏️' },
  'ai': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6', label: 'AI', icon: '🤖' },  // Blue
  'deep-ai': { bg: '#ffedd5', text: '#9a3412', border: '#f97316', label: 'Deep AI', icon: '🔬' },  // Orange
  'pattern': { bg: '#f1f5f9', text: '#475569', border: '#94a3b8', label: 'Pattern', icon: '📋' },
};

type GroupBy = 'none' | 'component' | 'file' | 'screen' | 'triggerType' | 'category';
type SortBy = 'name' | 'steps' | 'type';

interface InteractionItem {
  flow: E2EFlowPath;
  step: E2EFlowStep;
  component?: string;
  file?: string;
  screen?: string;
  modalComponentName?: string;  // Issue #348: Modal component name for grouping
  exportGroupName?: string;     // Issue #349: Export group name for grouping
}

// Issue #348: Grouped modal interactions
interface ModalGroup {
  name: string;
  filePath?: string;
  interactions: InteractionItem[];
}

// Issue #349: Grouped export interactions
interface ExportGroup {
  name: string;
  filePath?: string;
  interactions: InteractionItem[];
}

// ============= Constants =============

const TRIGGER_TYPE_LABELS: Record<FlowTriggerType, string> = {
  click: 'Click',
  submit: 'Form Submit',
  navigate: 'Navigation',
  load: 'Page Load',
  event: 'Custom Event',
  timer: 'Timer',
  websocket: 'WebSocket',
  fetch: 'Data Fetch',
  gesture: 'Gesture',
  realtime: 'Real-time',
  file: 'File Operation',
};

const TRIGGER_TYPE_ICONS: Record<FlowTriggerType, string> = {
  click: '👆',
  submit: '📝',
  navigate: '🔗',
  load: '📄',
  event: '⚡',
  timer: '⏱️',
  websocket: '🔌',
  fetch: '📡',
  gesture: '👋',
  realtime: '🔄',
  file: '📁',
};

// Issue #344: Interaction category labels and styling
const CATEGORY_LABELS: Record<InteractionCategory, string> = {
  'navigation': 'Navigation',
  'toggle': 'Toggle',
  'accordion-toggle': 'Accordion',   // Issue #357
  'local-state': 'Local State',
  'modal-dialog': 'Modal/Dialog',
  'export-download': 'Export/Download',
  'clipboard': 'Clipboard',
  'form-submit': 'Form Submit',
  'api-bound': 'API Call',
  'context-menu': 'Context Menu',           // Issue #452
  'canvas-interaction': 'Canvas',           // Issue #452
  'keyboard-shortcut': 'Keyboard',          // Issue #452
  'undo-redo': 'Undo/Redo',                 // Issue #452
  'file-operation': 'File Operation',       // Issue #453
  'browser-dialog': 'Browser Dialog',       // Issue #453
  'analytics-tracking': 'Analytics',        // Issue #453
  'tooltip-popover': 'Tooltip/Popover',     // Issue #453
  'tab-navigation': 'Tab Navigation',       // Issue #453
  'infinite-scroll': 'Infinite Scroll',     // Issue #453
  'slider-carousel': 'Slider/Carousel',     // Issue #453
  'unknown': 'Unknown',
};

const CATEGORY_COLORS: Record<InteractionCategory, { bg: string; text: string; border: string }> = {
  'navigation': { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
  'toggle': { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  'accordion-toggle': { bg: '#fef9c3', text: '#854d0e', border: '#eab308' },  // Issue #357: Yellow for accordion
  'local-state': { bg: '#e0e7ff', text: '#3730a3', border: '#6366f1' },
  'modal-dialog': { bg: '#fce7f3', text: '#9d174d', border: '#ec4899' },
  'export-download': { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  'clipboard': { bg: '#f3e8ff', text: '#6b21a8', border: '#a855f7' },
  'form-submit': { bg: '#ffedd5', text: '#9a3412', border: '#f97316' },
  'api-bound': { bg: '#dcfce7', text: '#166534', border: '#22c55e' },
  'context-menu': { bg: '#fef3c7', text: '#78350f', border: '#d97706' },      // Issue #452: Amber
  'canvas-interaction': { bg: '#cffafe', text: '#155e75', border: '#06b6d4' }, // Issue #452: Cyan
  'keyboard-shortcut': { bg: '#f0fdf4', text: '#14532d', border: '#22c55e' }, // Issue #452: Green
  'undo-redo': { bg: '#fef2f2', text: '#7f1d1d', border: '#ef4444' },         // Issue #452: Red
  'file-operation': { bg: '#f0f9ff', text: '#0c4a6e', border: '#0284c7' },    // Issue #453: Sky
  'browser-dialog': { bg: '#fdf4ff', text: '#701a75', border: '#d946ef' },    // Issue #453: Fuchsia
  'analytics-tracking': { bg: '#ecfeff', text: '#164e63', border: '#22d3ee' }, // Issue #453: Cyan
  'tooltip-popover': { bg: '#fffbeb', text: '#78350f', border: '#fbbf24' },   // Issue #453: Amber
  'tab-navigation': { bg: '#eef2ff', text: '#3730a3', border: '#818cf8' },    // Issue #453: Indigo
  'infinite-scroll': { bg: '#f5f3ff', text: '#5b21b6', border: '#8b5cf6' },   // Issue #453: Violet
  'slider-carousel': { bg: '#fdf2f8', text: '#9d174d', border: '#f472b6' },   // Issue #453: Pink
  'unknown': { bg: '#f1f5f9', text: '#475569', border: '#94a3b8' },
};

// Shared/layout component patterns that appear across multiple screens
// Shared/layout component patterns that appear across multiple screens.
//
// R1 catch (issue#6) — the original regexes were `/\/Header/i` and friends,
// which matched the substring `/Header` ANYWHERE in the path. That
// silently classified `src/screen/Notification.js` (a real RN screen)
// and `src/components/HeaderMenu.tsx` (a domain-specific header
// component) as Shared/Layout. Tighten to a path-segment match:
//
//   - lowercase generic buckets (`/layout/`, `/common/`, `/shared/`,
//     `/ui/`) — already correctly slash-bounded
//   - PascalCase layout-element names (`Header`, `Modal`, ...) — match
//     either as a folder (`/Header/...`) OR as a bare top-level file
//     (`/Header.tsx`), not as a prefix of another identifier
//     (`HeaderMenu`, `Notification.js`)
//
// This also closes the longstanding backend↔frontend asymmetry where
// `src/Modal/ProfileScreen.tsx` was "Profile" on backend and
// "Shared / Layout" on frontend — the tightened regex no longer
// catches that case as shared, and the backend's existing priority-1
// `XxxScreen.tsx` matcher returns "Profile" in agreement.
const SHARED_COMPONENT_PATTERNS = [
  /\/layout\//i,
  /\/common\//i,
  /\/shared\//i,
  /\/ui\//i,
  /\/(?:Header|Footer|Sidebar|Nav|Modal|Dialog|Toast|Notification|Drawer|Sheet|Popover|Dropdown)(?:\/|\.[jt]sx?$)/i,
];

/**
 * Infer screen/page name from file path
 * Uses folder structure conventions to determine which screen a component belongs to
 *
 * veodiagram-issues#2: extended with snake/kebab/camel filename and
 * `XxxComponent` class-suffix detection so vanilla-TS class-based
 * UIs (`auth_signin.ts`, `MyAccountComponent.ts`) get readable
 * screen labels instead of falling through to "Other".
 */
/**
 * veodiagram-issues#15 + #19 — screen-name override registry,
 * populated via `GET /projects/:id/screen-overrides`. Each file may
 * have multiple entries (one per route, plus optionally a
 * file-level fallback under routePath=''). The catalog stores them
 * grouped by filePath so it can:
 *   - return the file-level entry when no route context is given
 *   - return the highest-precedence per-route entry as a fallback
 *   - (future) render distinct screen rows for each per-route entry
 */
export interface ScreenOverrideEntry {
  routePath: string;          // '' = file-level
  name: string;
  source: 'ai' | 'user' | 'cluster';
}

export type ScreenOverrideMap = Map<string, ScreenOverrideEntry[]>;

function inferScreenFromPath(
  filePath: string | undefined,
  overrides?: ScreenOverrideMap,
): string {
  if (!filePath) return 'Unknown';

  // veodiagram-issues#15 + #19 priority 0: registry override.
  // Prefer the file-level entry; fall back to any per-route entry
  // (server-rendered files where Phase 5 only emits per-route names).
  // R1 finding #7 — short-circuit when entries.length === 1 (the
  // common case after #19 for non-multi-route files) so the catalog
  // doesn't allocate + sort on every render.
  const pick = (entries: ScreenOverrideEntry[] | undefined): string | undefined => {
    if (!entries || entries.length === 0) return undefined;
    if (entries.length === 1) return entries[0].name;
    const fileLevel = entries.find(e => e.routePath === '');
    if (fileLevel) return fileLevel.name;
    // Pick highest-precedence per-route entry (user > ai > cluster).
    const weight = (s: ScreenOverrideEntry['source']): number =>
      s === 'user' ? 3 : s === 'ai' ? 2 : 1;
    let best = entries[0];
    let bestW = weight(best.source);
    for (let i = 1; i < entries.length; i++) {
      const w = weight(entries[i].source);
      if (w > bestW) { best = entries[i]; bestW = w; }
    }
    return best.name;
  };

  if (overrides && overrides.size > 0) {
    const direct = pick(overrides.get(filePath));
    if (direct) return direct;
  }

  // Normalize path separators
  const normalizedPath = filePath.replace(/\\/g, '/');

  if (overrides && overrides.size > 0 && normalizedPath !== filePath) {
    const normalizedHit = pick(overrides.get(normalizedPath));
    if (normalizedHit) return normalizedHit;
  }

  // veodiagram-issues#2: third-party libs leak into the catalog as
  // fake "screens" (jquery, modernizr, etc.). The analyzer's
  // server-side filter (`apps/api/src/services/analysis/analyzer.ts`
  // SKIP_DIR_NAMES + SKIP_FILE_PATTERNS) drops most of them, but
  // some still arrive via cached analysis results. Guard at the UI
  // layer too — these are never user-authored screens.
  if (/\/(?:libs|node_modules|vendor)\//i.test(normalizedPath)) {
    return 'External Library';
  }
  if (/\.(?:min|bundle)\.[jt]sx?$/i.test(normalizedPath) || /-(?:min|bundle)\.[jt]sx?$/i.test(normalizedPath)) {
    return 'External Library';
  }

  // Check if this is a shared/layout component.
  //
  // R1 catch (issue#6): the SHARED check yields to the screen-folder
  // check below — a file like `src/screen/Notification.js` is a real
  // screen (it's inside a `/screen[s]/` directory) and must not be
  // collapsed into Shared/Layout just because its bare filename
  // matches a layout-element name. Skip SHARED entirely when the
  // file lives under `/pages/`, `/screens/`, `/screen/`, or
  // `/views/`.
  const isInScreenFolder = /\/(?:pages|screens?|views)\//i.test(normalizedPath);
  if (!isInScreenFolder) {
    for (const pattern of SHARED_COMPONENT_PATTERNS) {
      if (pattern.test(normalizedPath)) {
        return 'Shared / Layout';
      }
    }
  }

  // issue#6 (R1 catch): mirror the backend's priority-1 `XxxPage.tsx` /
  // `XxxScreen.tsx` filename matcher so a file at e.g.
  // `src/auth/ProfileScreen.tsx` is labeled "Profile" by both sides
  // (otherwise the backend says "Profile" via fileMatch, the frontend
  // would fall through to the parent-folder rule and say "Auth").
  //
  // R2 catch on 0294790: when the captured stem itself ends with a
  // container name (`ProfileModalScreen.tsx` → stem="ProfileModal"),
  // collapse to the container so all modals share a catalog bucket —
  // same semantics the backend uses.
  const xxxScreenFileMatch = normalizedPath.match(/\/([A-Z][a-zA-Z0-9]+)(?:Page|Screen)\.(?:tsx?|jsx?)$/);
  if (xxxScreenFileMatch) {
    const stem = xxxScreenFileMatch[1];
    const CONTAINER_SUFFIXES = ['Modal', 'Dialog', 'Drawer', 'Sheet', 'Popover', 'Dropdown'];
    for (const suffix of CONTAINER_SUFFIXES) {
      if (stem.endsWith(suffix)) return suffix;
    }
    return formatScreenName(stem);
  }

  // Try to extract from pages/ or views/ or screen[s]/ folder.
  // `screens?` admits BOTH `/screens/` (plural — React Native is mixed,
  // Next.js convention) AND `/screen/` (singular — issue#6 / appfe-v2).
  const pagesMatch = normalizedPath.match(/\/(?:pages|views|screens?)\/([^/]+)/i);
  if (pagesMatch) {
    // Clean up the page name
    let pageName = pagesMatch[1];
    // Remove common suffixes
    pageName = pageName.replace(/(?:Page|View|Screen)\.tsx?$/i, '');
    pageName = pageName.replace(/\.tsx?$/i, '');
    // Handle index files - use parent folder name
    if (pageName.toLowerCase() === 'index') {
      const parentMatch = normalizedPath.match(/\/(?:pages|views|screens?)\/([^/]+)\/index/i);
      if (parentMatch) {
        pageName = parentMatch[1];
      }
    }
    return formatScreenName(pageName);
  }

  // Try to extract from components/ subfolder (e.g., components/Dashboard/*)
  const componentsMatch = normalizedPath.match(/\/components\/([^/]+)\//i);
  if (componentsMatch) {
    const folderName = componentsMatch[1];
    // Skip generic folders
    if (!['ui', 'common', 'shared', 'layout', 'lib', 'utils'].includes(folderName.toLowerCase())) {
      return formatScreenName(folderName);
    }
  }

  // veodiagram-issues#2: file directly under /components/ (no
  // per-screen subfolder) — derive the screen from the
  // `XxxComponent` class-name suffix. Used by vanilla-TS UIs
  // following a class-per-file convention.
  //   /components/MyAccountComponent.ts → "My Account"
  //   /components/ErrorsComponent.ts    → "Errors"
  const componentFileMatch = normalizedPath.match(/\/components\/([A-Z][a-zA-Z0-9]+?)Component\.(?:tsx?|jsx?)$/);
  if (componentFileMatch) {
    return formatScreenName(componentFileMatch[1]);
  }

  // Try to extract from src/ direct subfolders (e.g., src/Admin/*)
  const srcMatch = normalizedPath.match(/\/src\/([^/]+)\//i);
  if (srcMatch) {
    const folderName = srcMatch[1];
    // Skip common non-screen folders
    if (!['components', 'hooks', 'utils', 'lib', 'services', 'api', 'types', 'styles', 'assets'].includes(folderName.toLowerCase())) {
      return formatScreenName(folderName);
    }
  }

  // veodiagram-issues#2: snake/kebab/camel file stem directly under
  // a src-like root. Catches the customer's `auth_signin.ts`,
  // `my_training.ts`, `auth-reset-password.ts` layout where every
  // page lives flat in `src/` without a per-screen subdir.
  //   /src/auth_signin.ts        → "Auth Signin"
  //   /src/my_training.ts        → "My Training"
  //   /src/account-settings.ts   → "Account Settings"
  // Skipped (generic stems):
  //   /src/index.ts, /src/api.ts, /src/utils.ts
  const flatStemMatch = normalizedPath.match(/\/src\/([a-zA-Z0-9_-]+)\.(?:tsx?|jsx?)$/i);
  if (flatStemMatch) {
    const stem = flatStemMatch[1];
    const GENERIC_STEMS = new Set([
      'index', 'main', 'app', 'api', 'utils', 'util', 'helpers', 'helper',
      'types', 'constants', 'config', 'globals', 'shared', 'common',
    ]);
    if (!GENERIC_STEMS.has(stem.toLowerCase()) && stem.length > 1) {
      return formatScreenName(stem);
    }
  }

  // Fallback: use the parent folder of the file
  const parts = normalizedPath.split('/');
  if (parts.length >= 2) {
    const parentFolder = parts[parts.length - 2];
    if (parentFolder && parentFolder !== 'src') {
      // veodiagram-issues#2: skip generic asset bucket names —
      // `assets`, `dist`, `build` shouldn't surface as screens.
      if (['assets', 'dist', 'build', 'static', 'public'].includes(parentFolder.toLowerCase())) {
        return 'Other';
      }
      return formatScreenName(parentFolder);
    }
  }

  return 'Other';
}

/**
 * Format a folder/file name into a readable screen name
 *
 * veodiagram-issues#2: extended to preserve acronym runs so a name
 * like `JADEDocs` becomes "JADE Docs" rather than "J A D E Docs".
 * Kept byte-identical to the backend copy in
 * `apps/api/src/services/analysis/generators/e2eFlowGenerator.ts`'s
 * `formatScreenName` so labels look the same regardless of
 * whether they came from the server-side `screen` field or the
 * client-side fallback.
 */
function formatScreenName(name: string): string {
  // Insert space at camelCase boundaries while preserving acronym
  // runs: lower→Upper splits ("aA" → "a A"), and Upper-run followed
  // by Upper+lower also splits ("AAa" → "A Aa").
  const withSpaces = name.replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  const normalized = withSpaces.replace(/[-_]/g, ' ');
  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Infer component name from file path (Issue #383)
 * Uses file name as component name, handles index files by using parent directory
 */
function inferComponentFromPath(filePath: string | undefined): string | undefined {
  if (!filePath) return undefined;
  const parts = filePath.split('/');
  const fileName = parts.pop()?.replace(/\.(tsx?|jsx?)$/, '');
  // Handle index files - use parent directory name
  if (fileName === 'index') {
    return parts[parts.length - 1];
  }
  return fileName;
}

// ============= Helper Components =============

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Search...'}
        className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400"
      />
      <svg
        className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
  );
}

function FilterChip({
  label,
  isActive,
  onClick,
  icon,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  icon?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-all
        ${isActive
          ? 'bg-blue-100 dark:bg-blue-900 border-blue-500 text-blue-700 dark:text-blue-300'
          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-400'
        }
      `}
    >
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );
}

// Issue #348: Modal group card showing all interactions within a modal
function ModalGroupCard({
  group,
  onSelectInteraction,
  isExpanded,
  onToggle,
}: {
  group: ModalGroup;
  onSelectInteraction?: (item: InteractionItem) => void;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const fileName = group.filePath?.split('/').pop() || '';
  const categoryColors = CATEGORY_COLORS['modal-dialog'];

  // Get outcome labels for interactions
  const getOutcomeLabel = (item: InteractionItem) => {
    const outcomeType = (item.flow as { modalOutcomeType?: string }).modalOutcomeType;
    switch (outcomeType) {
      case 'dismiss': return 'Dismiss';
      case 'dismiss-reset': return 'Reset & Dismiss';
      case 'action-dismiss': return 'Action';
      case 'stay-open': return 'Stay Open';
      default: return 'Close';
    }
  };

  const getOutcomeColor = (item: InteractionItem) => {
    const outcomeType = (item.flow as { modalOutcomeType?: string }).modalOutcomeType;
    switch (outcomeType) {
      case 'dismiss': return { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' };
      case 'dismiss-reset': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'action-dismiss': return { bg: '#dcfce7', border: '#22c55e', text: '#166534' };
      case 'stay-open': return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
      default: return { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' };
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border-2 border-stone-400 dark:border-stone-600 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🚪</span>
          <div className="text-left">
            <h4 className="font-medium text-sm text-slate-800 dark:text-slate-200">
              {group.name}
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
              {fileName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: categoryColors.bg,
              color: categoryColors.text,
              border: `1px solid ${categoryColors.border}`,
            }}
          >
            Modal/Dialog
          </span>
          <span className="text-xs text-slate-400">
            {group.interactions.length} choices
          </span>
          <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/50" title="Complete Modal Flow">
            <svg className="w-2.5 h-2.5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </span>
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content with interaction choices */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
          {group.interactions.map((item, i) => {
            const outcomeColors = getOutcomeColor(item);
            return (
              <div
                key={i}
                className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                onClick={() => onSelectInteraction?.(item)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{TRIGGER_TYPE_ICONS[item.flow.triggerType]}</span>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {item.flow.triggerName}
                    </span>
                    {item.step.sourceLine && (
                      <span className="text-xs text-slate-400 ml-1 font-mono">
                        :{item.step.sourceLine}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: outcomeColors.bg,
                    color: outcomeColors.text,
                    border: `1px solid ${outcomeColors.border}`,
                  }}
                >
                  {getOutcomeLabel(item)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Issue #349: Export group card showing all export format options
function ExportGroupCard({
  group,
  onSelectInteraction,
  isExpanded,
  onToggle,
}: {
  group: ExportGroup;
  onSelectInteraction?: (item: InteractionItem) => void;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const fileName = group.filePath?.split('/').pop() || '';
  const categoryColors = CATEGORY_COLORS['export-download'];

  // Export format colors
  const getFormatColor = (item: InteractionItem) => {
    const format = (item.flow as { exportFormat?: string }).exportFormat;
    switch (format) {
      case 'image-raster': return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
      case 'image-vector': return { bg: '#e0e7ff', border: '#6366f1', text: '#4338ca' };
      case 'data': return { bg: '#dcfce7', border: '#22c55e', text: '#166534' };
      case 'code': return { bg: '#f3e8ff', border: '#9333ea', text: '#6b21a8' };
      case 'document': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      default: return { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' };
    }
  };

  const getFormatLabel = (item: InteractionItem) => {
    const format = (item.flow as { exportFormat?: string }).exportFormat;
    switch (format) {
      case 'image-raster': return 'PNG';
      case 'image-vector': return 'SVG';
      case 'data': return 'JSON';
      case 'code': return 'Code';
      case 'document': return 'PDF';
      default: return 'Export';
    }
  };

  const getFormatIcon = (item: InteractionItem) => {
    const format = (item.flow as { exportFormat?: string }).exportFormat;
    switch (format) {
      case 'image-raster':
      case 'image-vector': return '🖼️';
      case 'data': return '📊';
      case 'code': return '📝';
      case 'document': return '📄';
      default: return '📤';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border-2 border-emerald-400 dark:border-emerald-600 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📤</span>
          <div className="text-left">
            <h4 className="font-medium text-sm text-slate-800 dark:text-slate-200">
              {group.name}
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
              {fileName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: categoryColors.bg,
              color: categoryColors.text,
              border: `1px solid ${categoryColors.border}`,
            }}
          >
            Export/Download
          </span>
          <span className="text-xs text-slate-400">
            {group.interactions.length} formats
          </span>
          <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/50" title="Complete Export Flow">
            <svg className="w-2.5 h-2.5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </span>
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content with export options */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
          {group.interactions.map((item, i) => {
            const formatColors = getFormatColor(item);
            return (
              <div
                key={i}
                className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                onClick={() => onSelectInteraction?.(item)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{getFormatIcon(item)}</span>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {item.flow.triggerName}
                    </span>
                    {item.step.sourceLine && (
                      <span className="text-xs text-slate-400 ml-1 font-mono">
                        :{item.step.sourceLine}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: formatColors.bg,
                    color: formatColors.text,
                    border: `1px solid ${formatColors.border}`,
                  }}
                >
                  {getFormatLabel(item)}
                </span>
              </div>
            );
          })}
          <div className="text-xs text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-700">
            🎨 File downloaded to user's device
          </div>
        </div>
      )}
    </div>
  );
}

// Issue #362: Enhanced InteractionCard with inline name editing and AI naming
// Issue #368: View Context button for AI naming transparency
// Issue #385: Deep analysis button for full file context
// Issue #437: Enhancement summary badge for AI suggestions
function InteractionCard({
  item,
  onClick,
  projectId,
  resolvedName,
  aiAvailable,
  onGenerateAIName,
  onDeepAnalysis,
  onSaveName,
  onAcceptAIName,
  onRejectAIName,
  onViewContext,
  isGenerating,
  isDeepAnalysis,
  aiSuggestion,
  promptOverride,
  enhancementSummary,
}: {
  item: InteractionItem;
  onClick?: () => void;
  projectId?: string;
  resolvedName?: ResolvedName | null;
  aiAvailable?: boolean;
  onGenerateAIName?: () => void;
  onDeepAnalysis?: () => void;  // Issue #385: Deep analysis with full file
  onSaveName?: (name: string) => void;
  onAcceptAIName?: () => void;
  onRejectAIName?: () => void;
  onViewContext?: () => void;  // Issue #368: View AI context
  isGenerating?: boolean;
  isDeepAnalysis?: boolean;  // Issue #385: Flag for deep analysis mode
  aiSuggestion?: NamingResult | null;
  promptOverride?: { analysisDepth: string; hasCustomGuidelines: boolean; hasCustomSystemPrompt: boolean } | null;  // Issue #386
  enhancementSummary?: EnhancementSummary | null;  // Issue #437: Enhancement suggestion counts
}) {
  const { flow, step } = item;
  const colors = getStepTypeColors(step.type);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  // Extract file name and line number for source info display (#352)
  const fileName = item.file?.split('/').pop() || step.sourceFile?.split('/').pop();
  const lineNumber = step.sourceLine;
  const fullPath = item.file || step.sourceFile;

  // Get display name (resolved name or original)
  const displayName = resolvedName?.name || flow.name;
  // Issue #385: Detect deep analysis from reasoning prefix
  const isDeepAI = resolvedName?.source === 'ai' && resolvedName?.reasoning?.startsWith('[🔬 Deep Analysis]');
  const nameSource = isDeepAI ? 'deep-ai' : (resolvedName?.source || 'pattern');

  // Handle edit mode
  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(displayName);
    setIsEditing(true);
  };

  const handleSave = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (editValue.trim() && editValue !== displayName) {
      onSaveName?.(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave(e);
    } else if (e.key === 'Escape') {
      handleCancel(e);
    }
  };

  const handleGenerateAI = (e: React.MouseEvent) => {
    e.stopPropagation();
    onGenerateAIName?.();
  };

  // Issue #385: Handle deep analysis
  const handleDeepAnalysis = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeepAnalysis?.();
  };

  // Issue #368: Handle view context
  const handleViewContext = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewContext?.();
  };

  const handleAcceptAI = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAcceptAIName?.();
  };

  const handleRejectAI = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRejectAIName?.();
  };

  const handleEditAISuggestion = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (aiSuggestion) {
      setEditValue(aiSuggestion.suggestedName);
      setIsEditing(true);
    }
  };

  // Showing AI suggestion mode
  if (aiSuggestion && !isEditing) {
    return (
      <div
        className={`p-3 bg-white dark:bg-slate-800 rounded-lg border-2 ${
          aiSuggestion.usedDeepAnalysis
            ? 'border-orange-400 dark:border-orange-500'
            : 'border-blue-400 dark:border-blue-500'
        }`}
        onClick={onClick}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {/* Issue #385: Show different icon for deep analysis */}
              <span className="text-lg">{aiSuggestion.usedDeepAnalysis ? '🔬' : '🤖'}</span>
              <div>
                <p className={`text-[10px] font-medium ${
                  aiSuggestion.usedDeepAnalysis
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`}>
                  {aiSuggestion.usedDeepAnalysis ? 'Deep AI Suggested' : 'AI Suggested'}
                </p>
                <h4 className="font-medium text-sm text-slate-800 dark:text-slate-200">
                  "{aiSuggestion.suggestedName}"
                </h4>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Confidence: {Math.round(aiSuggestion.confidence * 100)}%
            </p>
            {aiSuggestion.reasoning && (
              <p className="text-[10px] text-slate-400 mt-1 italic">
                {aiSuggestion.reasoning}
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={handleAcceptAI}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900"
          >
            👍 Accept
          </button>
          <button
            onClick={handleRejectAI}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900"
          >
            👎 Reject
          </button>
          <button
            onClick={handleEditAISuggestion}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-600"
          >
            ✏️ Edit
          </button>
        </div>

        <p className="mt-2 text-[10px] text-slate-400">
          💡 Your feedback helps improve future suggestions
        </p>
      </div>
    );
  }

  return (
    <div
      className={`p-3 bg-white dark:bg-slate-800 rounded-lg border transition-all ${
        isEditing ? 'border-blue-400 dark:border-blue-600' :
        flow.complete
          ? 'border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600 cursor-pointer'
          : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer'
      }`}
      onClick={isEditing ? undefined : onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">{TRIGGER_TYPE_ICONS[flow.triggerType]}</span>

            {/* Issue #362: Editable name */}
            {isEditing ? (
              <div className="flex-1 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 px-2 py-1 text-sm border border-blue-400 dark:border-blue-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  maxLength={100}
                />
                <button
                  onClick={handleSave}
                  className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50 rounded"
                  title="Save (Enter)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={handleCancel}
                  className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  title="Cancel (Esc)"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <h4 className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">
                  {displayName}
                </h4>

                {/* Heuristic confidence indicator */}
                <ConfidenceBadge confidence={flow.confidence} reason={flow.confidenceReason} />

                {/* Complete E2E flow indicator */}
                {flow.complete && (
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/50" title="Complete E2E Flow">
                    <svg className="w-2.5 h-2.5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
                {/* Issue #859 / #878: stitch health indicator —
                    three-way classification. Prefer the new
                    `classification` field; fall back to `isClean` for
                    flows from older API responses. */}
                {(() => {
                  const fAny = flow as any;
                  const cls =
                    fAny.classification === 'clean' ||
                    fAny.classification === 'needs-review' ||
                    fAny.classification === 'no-api'
                      ? fAny.classification
                      : fAny.isClean === true
                        ? 'clean'
                        : fAny.isClean === false
                          ? 'needs-review'
                          : null;
                  if (cls === 'clean') {
                    return (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400" title="All API stitches confident">stitched</span>
                    );
                  }
                  if (cls === 'needs-review') {
                    return (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" title="Has ambiguous or unresolved API stitches">needs review</span>
                    );
                  }
                  if (cls === 'no-api') {
                    return (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400" title="No API calls in this flow — nothing to stitch">no API calls</span>
                    );
                  }
                  return null;
                })()}

                {/* Issue #362: Edit and AI name buttons */}
                {/* Issue #368: View Context button */}
                {projectId && (
                  <div className="flex items-center gap-1 ml-auto" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={handleStartEdit}
                      className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                      title="Edit name"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    {/* Issue #368: View AI Naming Context button */}
                    {/* Issue #386: Show depth indicator if prompt override exists */}
                    {onViewContext && (
                      <button
                        onClick={handleViewContext}
                        className={`p-1 rounded relative flex items-center gap-0.5 ${
                          promptOverride
                            ? 'text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                            : 'text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                        }`}
                        title={promptOverride
                          ? `AI naming settings (${promptOverride.analysisDepth})`
                          : 'AI naming settings'
                        }
                      >
                        {/* Sparkles icon — standard AI indicator */}
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                        </svg>
                        <span className="text-[10px] font-semibold leading-none">AI</span>
                        {/* Issue #386: Depth indicator badge */}
                        {promptOverride && (
                          <span className={`absolute -top-0.5 -right-0.5 text-[8px] leading-none ${
                            promptOverride.analysisDepth === 'light' ? 'text-blue-500' :
                            promptOverride.analysisDepth === 'deep' ? 'text-orange-500' :
                            'text-slate-500'
                          }`}>
                            {promptOverride.analysisDepth === 'light' && '⚡'}
                            {promptOverride.analysisDepth === 'standard' && '📋'}
                            {promptOverride.analysisDepth === 'deep' && '🔬'}
                          </span>
                        )}
                      </button>
                    )}
                    {/* Issue #362: Quick AI button (blue background) */}
                    {aiAvailable && (
                      <button
                        onClick={handleGenerateAI}
                        disabled={isGenerating}
                        className={`p-1 rounded border border-blue-500 ${
                          isGenerating && !isDeepAnalysis
                            ? 'bg-slate-200 cursor-not-allowed'
                            : 'bg-blue-100 hover:bg-blue-200'
                        }`}
                        title={isGenerating && !isDeepAnalysis ? 'Generating...' : 'Quick AI name'}
                      >
                        {isGenerating && !isDeepAnalysis ? (
                          <svg className="w-3.5 h-3.5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <span className="text-sm">🤖</span>
                        )}
                      </button>
                    )}
                    {/* Issue #385: Deep analysis button (orange background for contrast with blue AI button) */}
                    {aiAvailable && (
                      <button
                        onClick={handleDeepAnalysis}
                        disabled={isGenerating}
                        className={`p-1 rounded border border-orange-500 ${
                          isGenerating && isDeepAnalysis
                            ? 'bg-slate-200 cursor-not-allowed'
                            : 'bg-orange-100 hover:bg-orange-200'
                        }`}
                        title={isGenerating && isDeepAnalysis ? 'Deep analyzing...' : 'Deep AI analysis (uses full file)'}
                      >
                        {isGenerating && isDeepAnalysis ? (
                          <svg className="w-3.5 h-3.5 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <span className="text-sm">🔬</span>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {flow.triggerName}
            </p>
            {/* Issue #362: Name source badge */}
            {projectId && resolvedName && NAME_SOURCE_COLORS[nameSource] && (
              <span
                className="text-[9px] px-1 py-0.5 rounded"
                style={{
                  backgroundColor: NAME_SOURCE_COLORS[nameSource].bg,
                  color: NAME_SOURCE_COLORS[nameSource].text,
                  border: `1px solid ${NAME_SOURCE_COLORS[nameSource].border}`,
                }}
                title={`Name source: ${nameSource}`}
              >
                {NAME_SOURCE_COLORS[nameSource].label}
              </span>
            )}
          </div>

          {/* Issue #422: Display Interaction IID */}
          {flow.interactionIID && (
            <div className="flex items-center gap-1 mt-0.5">
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(flow.interactionIID || '');
                }}
                title={`Click to copy: ${flow.interactionIID}`}
              >
                📍 {flow.interactionIID}
              </span>
            </div>
          )}

          {/* Source file info (#352) */}
          {fileName && (
            <div
              className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500"
              title={fullPath}
            >
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-mono truncate">
                {fileName}{lineNumber ? `:${lineNumber}` : ''}
              </span>
            </div>
          )}

          {/* Issue #367: Display AI reasoning after acceptance */}
          {resolvedName?.reasoning && (
            <p className="mt-1.5 text-[10px] text-indigo-600 dark:text-indigo-400 italic leading-tight">
              💡 {resolvedName.reasoning}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1">
          {/* Issue #344: Category badge */}
          {flow.interactionCategory && flow.interactionCategory !== 'unknown' && CATEGORY_COLORS[flow.interactionCategory] && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full"
              style={{
                backgroundColor: CATEGORY_COLORS[flow.interactionCategory].bg,
                color: CATEGORY_COLORS[flow.interactionCategory].text,
                border: `1px solid ${CATEGORY_COLORS[flow.interactionCategory].border}`,
              }}
            >
              {CATEGORY_LABELS[flow.interactionCategory] || flow.interactionCategory}
            </span>
          )}
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
          >
            {STEP_TYPE_LABELS[step.type]}
          </span>
          <span className="text-[10px] text-slate-400">
            {flow.steps.length} steps
          </span>
        </div>
      </div>

      {/* Flow preview */}
      <div className="mt-2 flex items-center gap-1 overflow-hidden">
        {flow.steps.slice(0, 5).map((s, i) => {
          const stepColors = getStepTypeColors(s.type);
          return (
            <div
              key={i}
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: stepColors.border }}
              title={s.name}
            />
          );
        })}
        {flow.steps.length > 5 && (
          <span className="text-[10px] text-slate-400 ml-1">
            +{flow.steps.length - 5}
          </span>
        )}
      </div>

      {/* Metadata */}
      <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-slate-400">
        {flow.touchedServices.length > 0 && (
          <span>🔌 {flow.touchedServices.length} services</span>
        )}
        {flow.touchedTables.length > 0 && (
          <span>💾 {flow.touchedTables.length} tables</span>
        )}
        {!flow.complete && (
          <span className="text-amber-500">UI-Only</span>
        )}
      </div>

      {/* Issue #437: AI Enhancement suggestion badge */}
      {enhancementSummary && enhancementSummary.totalCount > 0 && (
        <div className="mt-2">
          {enhancementSummary.applied ? (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
              ✓ All suggestions applied
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
              🤖 {enhancementSummary.totalCount} AI suggestion{enhancementSummary.totalCount !== 1 ? 's' : ''}
              {enhancementSummary.missedCount > 0 && (
                <span className="text-amber-600 dark:text-amber-400">
                  ({enhancementSummary.missedCount} missed{enhancementSummary.suggestedCount > 0 ? `, ${enhancementSummary.suggestedCount} new` : ''})
                </span>
              )}
              {enhancementSummary.missedCount === 0 && enhancementSummary.suggestedCount > 0 && (
                <span>({enhancementSummary.suggestedCount} new)</span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Issue #345-353: Enhanced interaction details */}
      {(() => {
        // Type definitions for enhanced fields
        interface NavigationTargetDetails {
          path?: string;
          component?: string;
          isDynamic?: boolean;
        }
        interface DomEffects {
          conditionalRender?: string[];
          cssClassChanges?: string[];
          affectedElements?: string[];
        }
        interface ClipboardContent {
          contentType?: string;
          preview?: string;
          confirmationUI?: string;
        }
        interface FormValidation {
          fields?: string[];
          validationRules?: string[];
          errorDisplay?: string;
        }
        interface ApiSummary {
          method?: string;
          endpoint?: string;
          requestBody?: string[];
          errorHandling?: string;
        }
        interface AffectedElement {
          elementId: string;
          changeType: string;
          description: string;
        }

        // Get step data with enhanced fields
        const stepData = step.data as Record<string, unknown>;
        const userInteraction = stepData?.userInteraction as Record<string, unknown> | undefined;

        // Extract enhanced fields from userInteraction or stepData with proper types
        const navigationTargetDetails = (stepData?.navigationTargetDetails || userInteraction?.navigationTargetDetails) as NavigationTargetDetails | undefined;
        const domEffects = (stepData?.domEffects || userInteraction?.domEffects) as DomEffects | undefined;
        const clipboardContent = (stepData?.clipboardContent || userInteraction?.clipboardContent) as ClipboardContent | undefined;
        const formValidation = (stepData?.formValidation || userInteraction?.formValidation) as FormValidation | undefined;
        const apiSummary = (stepData?.apiSummary || userInteraction?.apiSummary) as ApiSummary | undefined;
        const affectedElements = (stepData?.affectedElements || userInteraction?.affectedElements) as AffectedElement[] | undefined;

        const hasEnhancedData = navigationTargetDetails || domEffects || clipboardContent || formValidation || apiSummary || affectedElements;

        if (!hasEnhancedData) return null;

        return (
          <div className="mt-2 space-y-1.5">
            {/* Issue #345: Navigation target */}
            {navigationTargetDetails && (
              <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-1.5 text-[10px]">
                  <span>📱</span>
                  <span className="font-medium text-blue-700 dark:text-blue-300">
                    {navigationTargetDetails.component || 'Page'}
                  </span>
                  <span className="text-blue-600 dark:text-blue-400">
                    → {navigationTargetDetails.path}
                  </span>
                  {navigationTargetDetails.isDynamic && (
                    <span className="px-1 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded text-[9px]">
                      dynamic
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Issue #346: DOM effects */}
            {domEffects && (
              <div className="p-1.5 bg-cyan-50 dark:bg-cyan-900/20 rounded border border-cyan-200 dark:border-cyan-800">
                <div className="text-[10px] text-cyan-700 dark:text-cyan-300">
                  <span className="font-medium">👁️ DOM Effects:</span>
                  <div className="mt-0.5 space-y-0.5">
                    {(domEffects.conditionalRender || []).map((el, i) => (
                      <div key={i} className="pl-3 text-cyan-600 dark:text-cyan-400">• {el}</div>
                    ))}
                    {(domEffects.cssClassChanges || []).map((cls, i) => (
                      <div key={i} className="pl-3 text-cyan-600 dark:text-cyan-400">🎨 {cls}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Issue #350: Clipboard content */}
            {clipboardContent && (
              <div className="p-1.5 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                <div className="text-[10px] text-green-700 dark:text-green-300">
                  <span className="font-medium">📋 Copies to clipboard:</span>
                  <span className="ml-1 text-green-600 dark:text-green-400">
                    {clipboardContent.contentType || 'content'}
                  </span>
                  {clipboardContent.confirmationUI && (
                    <div className="mt-0.5 text-green-600 dark:text-green-400">
                      ✓ Shows: {clipboardContent.confirmationUI}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Issue #351: Form validation */}
            {formValidation && (
              <div className="p-1.5 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
                <div className="text-[10px] text-amber-700 dark:text-amber-300">
                  <span className="font-medium">📝 Form fields:</span>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {(formValidation.fields || []).map((field, i) => (
                      <span key={i} className="px-1 py-0.5 bg-amber-100 dark:bg-amber-800 rounded text-[9px]">
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Issue #352: API summary */}
            {apiSummary && (
              <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded border border-indigo-200 dark:border-indigo-800">
                <div className="text-[10px] text-indigo-700 dark:text-indigo-300">
                  <span className="font-medium">🔗 API:</span>
                  <span className="ml-1 font-mono text-indigo-600 dark:text-indigo-400">
                    {apiSummary.method} {apiSummary.endpoint}
                  </span>
                  {apiSummary.errorHandling && (
                    <span className="ml-1 text-indigo-500">✓ error handling</span>
                  )}
                </div>
              </div>
            )}

            {/* Issue #347: Affected elements */}
            {affectedElements && affectedElements.length > 0 && (
              <div className="p-1.5 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                <div className="text-[10px] text-slate-600 dark:text-slate-400">
                  <span className="font-medium">⚡ Effects:</span>
                  <div className="mt-0.5 space-y-0.5">
                    {affectedElements.slice(0, 3).map((el, i) => (
                      <div key={i} className="pl-3">
                        {el.elementId}: {el.description}
                      </div>
                    ))}
                    {affectedElements.length > 3 && (
                      <div className="pl-3 text-slate-400">+{affectedElements.length - 3} more</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Issue #353: Evidence display */}
      {step.evidence && step.evidence.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
          <div className="text-[10px] text-slate-400 dark:text-slate-500">
            <span className="font-medium">📄 Source:</span>
            {step.evidence.slice(0, 2).map((ev, i) => (
              <div key={i} className="font-mono ml-2 truncate" title={ev.filePath}>
                {ev.filePath?.split('/').slice(-2).join('/')}:{ev.lineStart}
              </div>
            ))}
            {step.evidence.length > 2 && (
              <span className="ml-2 text-slate-400">+{step.evidence.length - 2} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GroupHeader({
  title,
  count,
  isExpanded,
  onToggle,
}: {
  title: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
    >
      <span className="font-medium text-sm text-slate-700 dark:text-slate-300">
        {title}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {count}
        </span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </button>
  );
}

// ============= Main Component =============

export function InteractionCatalog({
  flows,
  onSelectFlow,
  onSelectStep: _onSelectStep,
  className = '',
  projectId,
  onNameUpdated,
  refreshTrigger,
}: InteractionCatalogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTriggerTypes, setSelectedTriggerTypes] = useState<Set<FlowTriggerType>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>('screen'); // Default to grouping by screen
  const [sortBy, setSortBy] = useState<SortBy>('name');
  // Track collapsed groups instead of expanded (all groups start expanded by default)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [expandedModals, setExpandedModals] = useState<Set<string>>(new Set());  // Issue #348
  const [expandedExports, setExpandedExports] = useState<Set<string>>(new Set());  // Issue #349

  // Issue #362: AI Naming state
  const [generatingFlowId, setGeneratingFlowId] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, NamingResult>>({});
  // Issue #385: Track deep analysis mode
  const [isDeepAnalysisMode, setIsDeepAnalysisMode] = useState(false);

  // Issue #368: Context preview modal state
  const [contextPreviewItem, setContextPreviewItem] = useState<InteractionItem | null>(null);

  // Issue #362: Use naming hooks only if projectId is provided
  // namesVersion is used to force re-renders when names are updated locally (fixes quick AI accept not updating)
  const { names: resolvedNames, fetchAllNames, updateLocalName, version: namesVersion } = useResolvedNames(projectId || '');
  const { generateName, isGenerating } = useAINaming(projectId || '');
  const { submitFeedback } = useNameFeedback(projectId || '');
  const { status: aiStatus, checkStatus } = useAIStatus(projectId || '');

  // Issue #386: Track per-interaction prompt overrides
  const [promptOverrides, setPromptOverrides] = useState<Record<string, {
    analysisDepth: string;
    hasCustomGuidelines: boolean;
    hasCustomSystemPrompt: boolean;
  }>>({});

  // Issue #437: Track enhancement summaries for flow cards
  const [enhancementSummaries, setEnhancementSummaries] = useState<Record<string, EnhancementSummary>>({});

  // veodiagram-issues#15 — screen-name override registry.
  // Populated via GET /projects/:id/screen-overrides on mount and
  // refetched after a successful refine job. Fed into
  // `inferScreenFromPath` at priority 0 so AI / user / cluster
  // overrides win over the heuristic ladder.
  const [screenOverrides, setScreenOverrides] = useState<ScreenOverrideMap>(() => new Map());

  // #15 — checkbox-driven file selection for the "Refine with AI" CTA.
  // Each catalog row contributes its source file; the master "Select all"
  // toggles the entire current filtered view.
  const [selectedFilePaths, setSelectedFilePaths] = useState<Set<string>>(new Set());

  // #15 — track in-flight refine job for the inline progress indicator.
  type RefineStatus = {
    jobId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    stage?: string;
    message?: string;
    error?: string;
  };
  const [refineStatus, setRefineStatus] = useState<RefineStatus | null>(null);

  // Memoize resolved names with version to ensure React tracks state changes
  // This fixes the issue where quick AI accept doesn't update the display immediately
  const memoizedResolvedNames = useMemo(() => {
    // namesVersion dependency ensures this recomputes when updateLocalName is called
    return resolvedNames;
  }, [resolvedNames, namesVersion]);

  // Issue #362: Fetch resolved names and AI status when projectId changes or refreshTrigger updates
  useEffect(() => {
    if (projectId) {
      fetchAllNames();
      checkStatus();
    }
  }, [projectId, refreshTrigger, fetchAllNames, checkStatus]);

  // veodiagram-issues#15 — fetch the screen-name override registry.
  // Also re-fetches after a successful refine job (see the poller below).
  // R2 finding #F3 — guard the async setState against unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const refetchScreenOverrides = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await api.getScreenOverrides(projectId);
      if (!mountedRef.current) return;
      // veodiagram-issues#19 — group by filePath; each file may have
      // multiple per-route entries plus an optional file-level fallback.
      const next: ScreenOverrideMap = new Map();
      for (const row of data.overrides) {
        if (!row.filePath || !row.name) continue;
        const list = next.get(row.filePath) ?? [];
        list.push({
          routePath: row.routePath ?? '',
          name: row.name,
          source: row.source,
        });
        next.set(row.filePath, list);
      }
      setScreenOverrides(next);
    } catch (err) {
      console.error('Failed to load screen overrides:', err);
    }
  }, [projectId]);

  useEffect(() => {
    refetchScreenOverrides();
  }, [refetchScreenOverrides, refreshTrigger]);

  // #15 — poll the refine job until it terminates.
  // R1 finding #8 — depend only on jobId + terminal-flag so the
  // interval is set up ONCE per job, not torn down + recreated on
  // each tick when setRefineStatus fires.
  const refineJobId = refineStatus?.jobId ?? null;
  const refineTerminal =
    refineStatus?.status === 'completed' || refineStatus?.status === 'failed';
  useEffect(() => {
    if (!refineJobId || refineTerminal) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const job = await api.getAnalysisStatus(refineJobId);
        if (cancelled) return;
        const r = (job.result ?? {}) as { stage?: string; message?: string };
        setRefineStatus({
          jobId: refineJobId,
          status: job.status as RefineStatus['status'],
          progress: job.progress ?? 0,
          stage: r.stage,
          message: r.message,
          error: job.error ?? undefined,
        });
        if (job.status === 'completed') {
          await refetchScreenOverrides();
        }
      } catch (err) {
        if (!cancelled) {
          setRefineStatus(s => (s ? { ...s, status: 'failed', error: String(err) } : s));
        }
      }
    };
    const intervalId = window.setInterval(tick, 2_000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [refineJobId, refineTerminal, refetchScreenOverrides]);

  // Issue #386: Fetch prompt overrides when projectId changes
  useEffect(() => {
    async function loadPromptOverrides() {
      if (!projectId) return;
      try {
        const response = await fetch(`/api/projects/${projectId}/interactions/prompt-overrides`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setPromptOverrides(data.overrides || {});
        }
      } catch (error) {
        console.error('Failed to load prompt overrides:', error);
      }
    }
    loadPromptOverrides();
  }, [projectId, refreshTrigger]);

  // Issue #437: Fetch enhancement summaries when projectId changes
  useEffect(() => {
    async function loadEnhancementSummaries() {
      if (!projectId) return;
      try {
        const data = await api.getE2EFlowEnhancementSummary(projectId);
        setEnhancementSummaries(data.flows || {});
      } catch (error) {
        console.error('Failed to load enhancement summaries:', error);
      }
    }
    loadEnhancementSummaries();
  }, [projectId, refreshTrigger]);

  // Issue #348: Toggle modal group expansion
  const toggleModalExpansion = useCallback((modalName: string) => {
    setExpandedModals(prev => {
      const next = new Set(prev);
      if (next.has(modalName)) {
        next.delete(modalName);
      } else {
        next.add(modalName);
      }
      return next;
    });
  }, []);

  // Issue #349: Toggle export group expansion
  const toggleExportExpansion = useCallback((exportName: string) => {
    setExpandedExports(prev => {
      const next = new Set(prev);
      if (next.has(exportName)) {
        next.delete(exportName);
      } else {
        next.add(exportName);
      }
      return next;
    });
  }, []);

  // Extract interaction items from flows
  const interactionItems = useMemo(() => {
    const items: InteractionItem[] = [];

    flows.forEach((flow) => {
      // Get the first user-action step as the main interaction
      const userActionStep = flow.steps.find((s) => s.type === 'user-action');
      const firstStep = userActionStep || flow.steps[0];

      if (firstStep) {
        const stepData = firstStep.data as Record<string, unknown> || {};
        const userInteraction = stepData.userInteraction as Record<string, unknown> | undefined;
        const flowExtended = flow as { modalComponentName?: string; exportGroupName?: string };
        // Issue #383: Fallback chain for component extraction
        const component = (stepData.component as string | undefined)
          || (userInteraction?.component as string | undefined)
          || inferComponentFromPath(firstStep.sourceFile);
        items.push({
          flow,
          step: firstStep,
          component,
          file: firstStep.sourceFile,
          screen: inferScreenFromPath(firstStep.sourceFile, screenOverrides),
          modalComponentName: flowExtended.modalComponentName,  // Issue #348
          exportGroupName: flowExtended.exportGroupName,        // Issue #349
        });
      }
    });

    return items;
  }, [flows, screenOverrides]);

  // Issue #348 & #349: Group modal and export interactions together
  const { modalGroups, exportGroups, regularItems } = useMemo(() => {
    const modals = new Map<string, ModalGroup>();
    const exports = new Map<string, ExportGroup>();
    const regular: InteractionItem[] = [];

    for (const item of interactionItems) {
      if (item.modalComponentName) {
        // Issue #348: Group modal interactions
        const existing = modals.get(item.modalComponentName);
        if (existing) {
          existing.interactions.push(item);
        } else {
          modals.set(item.modalComponentName, {
            name: item.modalComponentName,
            filePath: item.file,
            interactions: [item],
          });
        }
      } else if (item.exportGroupName) {
        // Issue #349: Group export interactions
        const existing = exports.get(item.exportGroupName);
        if (existing) {
          existing.interactions.push(item);
        } else {
          exports.set(item.exportGroupName, {
            name: item.exportGroupName,
            filePath: item.file,
            interactions: [item],
          });
        }
      } else {
        regular.push(item);
      }
    }

    return {
      modalGroups: Array.from(modals.values()),
      exportGroups: Array.from(exports.values()),
      regularItems: regular,
    };
  }, [interactionItems]);

  // Issue #348: Filter modal groups (Issue #422: Include IID in search)
  const filteredModalGroups = useMemo(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return modalGroups.filter(group => {
        // Check if group name matches
        if (group.name.toLowerCase().includes(query)) return true;
        // Check if any interaction matches
        return group.interactions.some(item => {
          const searchableText = [
            item.flow.name,
            item.flow.triggerName,
            item.flow.interactionIID,  // Issue #422: Search by IID
            item.flow.screen,          // Issue #422: Search by screen
            item.file,
          ].filter(Boolean).join(' ').toLowerCase();
          return searchableText.includes(query);
        });
      });
    }
    return modalGroups;
  }, [modalGroups, searchQuery]);

  // Issue #349: Filter export groups (Issue #422: Include IID in search)
  const filteredExportGroups = useMemo(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return exportGroups.filter(group => {
        // Check if group name matches
        if (group.name.toLowerCase().includes(query)) return true;
        // Check if any interaction matches
        return group.interactions.some(item => {
          const searchableText = [
            item.flow.name,
            item.flow.triggerName,
            item.flow.interactionIID,  // Issue #422: Search by IID
            item.flow.screen,          // Issue #422: Search by screen
            item.file,
          ].filter(Boolean).join(' ').toLowerCase();
          return searchableText.includes(query);
        });
      });
    }
    return exportGroups;
  }, [exportGroups, searchQuery]);

  // veodiagram-issues#15 — file paths available for refinement
  // across the CURRENT filtered view. Distinct + non-empty.
  // (Computed against `regularItems` to also include hidden items
  // that the master "Select all" should grab — but for now we tie
  // the candidate pool to what's visible to avoid surprises.)
  const candidateFilePaths = useMemo(() => {
    const set = new Set<string>();
    for (const item of regularItems) {
      if (item.file) set.add(item.file);
    }
    return set;
  }, [regularItems]);

  const allCandidatesSelected = useMemo(
    () => candidateFilePaths.size > 0 && [...candidateFilePaths].every(p => selectedFilePaths.has(p)),
    [candidateFilePaths, selectedFilePaths],
  );

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedFilePaths(prev => {
      const next = new Set(prev);
      if (allCandidatesSelected) {
        for (const p of candidateFilePaths) next.delete(p);
      } else {
        for (const p of candidateFilePaths) next.add(p);
      }
      return next;
    });
  }, [allCandidatesSelected, candidateFilePaths]);

  // Estimated LLM call count based on Phase 2's batching (~10
  // files/call) plus Phase 1's routing-config call when a router
  // is present. Conservative; actual count is bounded by phase
  // candidate-count.
  // R2 finding #F4 — dollar amounts removed. Per-call cost
  // varies ~10× by model (Haiku vs Opus / qwen vs Claude) and we
  // can't compute it accurately without per-model pricing logic.
  // Show call count only; users can multiply by their own per-
  // call cost.
  const estimatedLlmCalls = useMemo(() => {
    const n = selectedFilePaths.size;
    if (n === 0) return 0;
    return Math.max(1, Math.ceil(n / 10) + 1);
  }, [selectedFilePaths]);

  const handleRefineWithAI = useCallback(async () => {
    if (!projectId || selectedFilePaths.size === 0) return;
    try {
      const { jobId } = await api.refineScreenNamesWithAI(
        projectId,
        [...selectedFilePaths],
      );
      setRefineStatus({ jobId, status: 'pending', progress: 0, stage: 'refine:queued' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setRefineStatus({
        jobId: '',
        status: 'failed',
        progress: 0,
        error: message,
      });
    }
  }, [projectId, selectedFilePaths]);

  const isRefineRunning = !!refineStatus && refineStatus.status !== 'completed' && refineStatus.status !== 'failed';

  // Filter items (regular items only - excludes modal and export groups)
  const filteredItems = useMemo(() => {
    let items = regularItems;  // Issue #348 & #349: Use regular items only

    // Search filter (Issue #422: Include IID in search)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item) => {
        const searchableText = [
          item.flow.name,
          item.flow.triggerName,
          item.flow.description,
          item.flow.interactionIID,  // Issue #422: Search by IID
          item.flow.screen,          // Issue #422: Search by screen
          item.step.name,
          item.component,
          item.file,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchableText.includes(query);
      });
    }

    // Trigger type filter
    if (selectedTriggerTypes.size > 0) {
      items = items.filter((item) => selectedTriggerTypes.has(item.flow.triggerType));
    }

    // Sort
    items = [...items].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.flow.name.localeCompare(b.flow.name);
        case 'steps':
          return b.flow.steps.length - a.flow.steps.length;
        case 'type':
          return a.flow.triggerType.localeCompare(b.flow.triggerType);
        default:
          return 0;
      }
    });

    return items;
  }, [regularItems, searchQuery, selectedTriggerTypes, sortBy]);  // Issue #348 & #349: Use regularItems

  // Group items
  const groupedItems = useMemo(() => {
    if (groupBy === 'none') {
      return { '': filteredItems };
    }

    const groups: Record<string, InteractionItem[]> = {};

    filteredItems.forEach((item) => {
      let groupKey = '';

      switch (groupBy) {
        case 'component':
          groupKey = item.component || 'Unknown Component';
          break;
        case 'file':
          groupKey = item.file || 'Unknown File';
          break;
        case 'screen':
          groupKey = item.screen || 'Unknown Screen';
          break;
        case 'triggerType':
          groupKey = TRIGGER_TYPE_LABELS[item.flow.triggerType];
          break;
        case 'category':
          // Issue #344: Group by interaction category
          groupKey = item.flow.interactionCategory
            ? CATEGORY_LABELS[item.flow.interactionCategory]
            : 'Unknown';
          break;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [filteredItems, groupBy]);

  // Issue #384: Derive whether all groups are expanded
  const allGroupsExpanded = useMemo(() => {
    // Regular groups are expanded if NOT in collapsedGroups (empty means all expanded)
    const allRegularExpanded = collapsedGroups.size === 0;
    // Modal/Export groups are expanded if they ARE in their respective Sets
    const allModalsExpanded = modalGroups.length === 0 || modalGroups.every(g => expandedModals.has(g.name));
    const allExportsExpanded = exportGroups.length === 0 || exportGroups.every(g => expandedExports.has(g.name));
    return allRegularExpanded && allModalsExpanded && allExportsExpanded;
  }, [collapsedGroups, modalGroups, expandedModals, exportGroups, expandedExports]);

  // Get available trigger types
  const availableTriggerTypes = useMemo(() => {
    const types = new Set<FlowTriggerType>();
    flows.forEach((flow) => types.add(flow.triggerType));
    return Array.from(types);
  }, [flows]);

  // Toggle trigger type filter
  const toggleTriggerType = useCallback((type: FlowTriggerType) => {
    setSelectedTriggerTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Toggle group expansion (tracks collapsed groups, so all start expanded)
  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);  // Was collapsed, now expand
      } else {
        next.add(groupKey);     // Was expanded, now collapse
      }
      return next;
    });
  }, []);

  // Issue #384: Expand all groups
  const expandAllGroups = useCallback(() => {
    // Clear collapsedGroups (all regular groups become expanded)
    setCollapsedGroups(new Set());

    // Expand all modal groups
    const allModalNames = modalGroups.map(g => g.name);
    setExpandedModals(new Set(allModalNames));

    // Expand all export groups
    const allExportNames = exportGroups.map(g => g.name);
    setExpandedExports(new Set(allExportNames));
  }, [modalGroups, exportGroups]);

  // Issue #384: Collapse all groups
  const collapseAllGroups = useCallback(() => {
    // Add all group keys to collapsedGroups
    const allGroupKeys = Object.keys(groupedItems);
    setCollapsedGroups(new Set(allGroupKeys));

    // Clear expandedModals (all collapsed)
    setExpandedModals(new Set());

    // Clear expandedExports (all collapsed)
    setExpandedExports(new Set());
  }, [groupedItems]);

  // Handle item click
  const handleItemClick = useCallback(
    (item: InteractionItem) => {
      onSelectFlow?.(item.flow);
    },
    [onSelectFlow]
  );

  // Issue #362: Handle AI name generation
  // Issue #376: Use rich context for better AI naming
  const handleGenerateAIName = useCallback(async (item: InteractionItem) => {
    if (!projectId) return;

    setGeneratingFlowId(item.flow.id);

    // Build rich context with all available information
    const { flow, step } = item;
    // Issue #378: Access handlerName and elementText from flow (using type assertion for new fields)
    const flowWithNames = flow as typeof flow & { handlerName?: string; elementText?: string };
    const context: NamingContext = {
      handlerFunction: flowWithNames.handlerName || step.name || flow.triggerName,
      triggerElement: flow.triggerName,
      triggerType: flow.triggerType,
      componentFile: item.file || step.sourceFile || '',
      stateChanges: flow.steps
        .filter(s => s.type === 'state-change')
        .map(s => s.name),
      // Enhanced context for better naming
      codeSnippet: step.evidence?.[0]?.snippet,
      screenName: item.screen,
      interactionCategory: flow.interactionCategory,
      flowDescription: flow.description,
      apiCalls: flow.steps
        .filter(s => s.type === 'api-call' || s.type === 'api-route')
        .map(s => s.name),
      touchedTables: flow.touchedTables?.length > 0 ? flow.touchedTables : undefined,
      touchedServices: flow.touchedServices?.length > 0 ? flow.touchedServices : undefined,
      isCompleteFlow: flow.complete,
      flowSteps: flow.steps.map(s => s.label || s.name),
      // Issue #376: Request component summary generation
      generateComponentSummary: true,
      // Issue #378: Pass element text and handler name for better naming
      elementText: flowWithNames.elementText,
      handlerName: flowWithNames.handlerName,
    };

    const result = await generateName(item.flow.id, context);
    if (result) {
      setAiSuggestions(prev => ({
        ...prev,
        [item.flow.id]: result,
      }));
    }
    setGeneratingFlowId(null);
  }, [projectId, generateName]);

  // Issue #385: Handle deep AI name analysis with full file context
  const handleDeepAnalysisName = useCallback(async (item: InteractionItem) => {
    if (!projectId) return;

    setGeneratingFlowId(item.flow.id);
    setIsDeepAnalysisMode(true);

    // Build rich context with all available information + deep analysis flags
    const { flow, step } = item;
    const flowWithNames = flow as typeof flow & { handlerName?: string; elementText?: string };
    const context: NamingContext = {
      handlerFunction: flowWithNames.handlerName || step.name || flow.triggerName,
      triggerElement: flow.triggerName,
      triggerType: flow.triggerType,
      componentFile: item.file || step.sourceFile || '',
      stateChanges: flow.steps
        .filter(s => s.type === 'state-change')
        .map(s => s.name),
      // Enhanced context for better naming
      codeSnippet: step.evidence?.[0]?.snippet,
      screenName: item.screen,
      interactionCategory: flow.interactionCategory,
      flowDescription: flow.description,
      apiCalls: flow.steps
        .filter(s => s.type === 'api-call' || s.type === 'api-route')
        .map(s => s.name),
      touchedTables: flow.touchedTables?.length > 0 ? flow.touchedTables : undefined,
      touchedServices: flow.touchedServices?.length > 0 ? flow.touchedServices : undefined,
      isCompleteFlow: flow.complete,
      flowSteps: flow.steps.map(s => s.label || s.name),
      generateComponentSummary: true,
      elementText: flowWithNames.elementText,
      handlerName: flowWithNames.handlerName,
      // Issue #385: Deep analysis flags
      useDeepAnalysis: true,
      readFullFile: true,
    };

    const result = await generateName(item.flow.id, context);
    if (result) {
      setAiSuggestions(prev => ({
        ...prev,
        [item.flow.id]: result,
      }));
    }
    setGeneratingFlowId(null);
    setIsDeepAnalysisMode(false);
  }, [projectId, generateName]);

  // Issue #362: Handle saving a custom name
  const handleSaveName = useCallback(async (item: InteractionItem, name: string) => {
    if (!projectId) return;

    // Update UI immediately (optimistic update)
    updateLocalName(item.flow.id, name, 'override');
    onNameUpdated?.(item.flow.id, name, 'override');

    try {
      const response = await fetch(
        `/api/projects/${projectId}/interactions/${item.flow.id}/name?originalName=${encodeURIComponent(item.flow.name)}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        }
      );

      if (!response.ok) {
        // Revert on failure
        fetchAllNames();
      }
    } catch (error) {
      console.error('Failed to save name:', error);
      // Revert on error
      fetchAllNames();
    }
  }, [projectId, updateLocalName, onNameUpdated, fetchAllNames]);

  // Issue #362: Handle accepting AI suggestion
  // Issue #385: Track deep analysis in reasoning
  const handleAcceptAIName = useCallback(async (item: InteractionItem) => {
    if (!projectId) return;

    const suggestion = aiSuggestions[item.flow.id];
    if (!suggestion) return;

    // Capture values before clearing
    const { suggestedName, confidence, reasoning, usedDeepAnalysis } = suggestion;

    // Issue #385: Prepend deep analysis indicator to reasoning if used
    const enrichedReasoning = usedDeepAnalysis && reasoning
      ? `[🔬 Deep Analysis] ${reasoning}`
      : reasoning;

    // Update local state with name and reasoning FIRST
    updateLocalName(item.flow.id, suggestedName, 'ai', enrichedReasoning);
    onNameUpdated?.(item.flow.id, suggestedName, 'ai');

    // Then clear the suggestion to switch to normal card view
    setAiSuggestions(prev => {
      const next = { ...prev };
      delete next[item.flow.id];
      return next;
    });

    try {
      // Submit feedback and save the name (includes reasoning) in background
      await submitFeedback(item.flow.id, {
        aiSuggestedName: suggestedName,
        aiConfidence: confidence,
        feedbackType: 'good',
        handlerName: item.step.name,
        triggerElement: item.flow.triggerName,
        componentFile: item.file || item.step.sourceFile,
        reasoning: enrichedReasoning,
      }, item.flow.name);
    } catch (error) {
      console.error('Failed to accept AI name:', error);
      // Revert UI state on error by re-fetching names
      fetchAllNames();
    }
  }, [projectId, aiSuggestions, submitFeedback, updateLocalName, onNameUpdated, fetchAllNames]);

  // Issue #362: Handle rejecting AI suggestion
  const handleRejectAIName = useCallback(async (item: InteractionItem) => {
    if (!projectId) return;

    const suggestion = aiSuggestions[item.flow.id];
    if (!suggestion) return;

    try {
      await submitFeedback(item.flow.id, {
        aiSuggestedName: suggestion.suggestedName,
        aiConfidence: suggestion.confidence,
        feedbackType: 'bad',
        handlerName: item.step.name,
        triggerElement: item.flow.triggerName,
        componentFile: item.file || item.step.sourceFile,
      }, item.flow.name);

      // Clear the suggestion
      setAiSuggestions(prev => {
        const next = { ...prev };
        delete next[item.flow.id];
        return next;
      });
    } catch (error) {
      console.error('Failed to reject AI name:', error);
    }
  }, [projectId, aiSuggestions, submitFeedback]);

  // Issue #368: Handle viewing context preview
  const handleViewContext = useCallback((item: InteractionItem) => {
    setContextPreviewItem(item);
  }, []);

  // Issue #368: Build context for an interaction
  const buildContextForItem = useCallback((item: InteractionItem): NamingContext => {
    const { flow, step } = item;
    return {
      handlerFunction: step.name || flow.triggerName,
      triggerElement: flow.triggerName,
      triggerType: flow.triggerType,
      componentFile: item.file || step.sourceFile || '',
      stateChanges: flow.steps
        .filter(s => s.type === 'state-change')
        .map(s => s.name),
      codeSnippet: step.evidence?.[0]?.snippet,
      screenName: item.screen,
      interactionCategory: flow.interactionCategory,
      flowDescription: flow.description,
      apiCalls: flow.steps
        .filter(s => s.type === 'api-call' || s.type === 'api-route')
        .map(s => s.name),
      touchedTables: flow.touchedTables?.length > 0 ? flow.touchedTables : undefined,
      touchedServices: flow.touchedServices?.length > 0 ? flow.touchedServices : undefined,
      isCompleteFlow: flow.complete,
      flowSteps: flow.steps.map(s => s.label || s.name),
    };
  }, []);

  // Issue #368: Handle generating name from context preview modal
  const handleGenerateFromContext = useCallback(async (context: NamingContext) => {
    if (!projectId || !contextPreviewItem) return;

    const result = await generateName(contextPreviewItem.flow.id, context);
    if (result) {
      setAiSuggestions(prev => ({
        ...prev,
        [contextPreviewItem.flow.id]: result,
      }));
    }
  }, [projectId, contextPreviewItem, generateName]);

  // Stats
  const stats = useMemo(() => {
    const complete = flows.filter((f) => f.complete).length;
    const totalSteps = flows.reduce((sum, f) => sum + f.steps.length, 0);
    return {
      total: flows.length,
      complete,
      incomplete: flows.length - complete,
      totalSteps,
    };
  }, [flows]);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-1">
          Interactions
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {stats.total} interactions, {stats.complete} complete E2E flows
          {stats.incomplete > 0 && ` (${stats.incomplete} UI-only)`}
        </p>
      </div>

      {/* Search and Filters */}
      <div className="p-4 space-y-3 border-b border-slate-200 dark:border-slate-700">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search interactions..."
        />

        {/* Trigger Type Filters */}
        {availableTriggerTypes.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {availableTriggerTypes.map((type) => (
              <FilterChip
                key={type}
                label={TRIGGER_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1)}
                icon={TRIGGER_TYPE_ICONS[type]}
                isActive={selectedTriggerTypes.has(type)}
                onClick={() => toggleTriggerType(type)}
              />
            ))}
          </div>
        )}

        {/* Group and Sort */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Group:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
            >
              <option value="none">None</option>
              <option value="screen">By Screen</option>
              <option value="category">By Category</option>
              <option value="component">By Component</option>
              <option value="file">By File</option>
              <option value="triggerType">By Trigger Type</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-slate-500">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
            >
              <option value="name">Name</option>
              <option value="steps">Step Count</option>
              <option value="type">Trigger Type</option>
            </select>
          </div>

          {/* Issue #384: Expand/Collapse All Toggle - only show when grouping is active */}
          {groupBy !== 'none' && (
            <button
              onClick={allGroupsExpanded ? collapseAllGroups : expandAllGroups}
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors border border-slate-300 dark:border-slate-600"
              title={allGroupsExpanded ? 'Collapse all groups' : 'Expand all groups'}
            >
              {allGroupsExpanded ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  <span>Collapse All</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span>Expand All</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* veodiagram-issues#15 — Refine with AI bar. Renders only when
          a projectId is set (no point on the unconnected preview). */}
      {projectId && (
        <div
          data-testid="refine-with-ai-bar"
          className="p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
        >
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allCandidatesSelected}
                onChange={toggleSelectAllVisible}
                disabled={candidateFilePaths.size === 0 || isRefineRunning}
                data-testid="refine-select-all"
              />
              <span className="text-slate-600 dark:text-slate-300">
                Select all visible ({candidateFilePaths.size})
              </span>
            </label>

            <div className="flex-1" />

            <span className="text-slate-500 dark:text-slate-400">
              {selectedFilePaths.size} selected
              {selectedFilePaths.size > 0 && (
                <>
                  {' · '}~{estimatedLlmCalls} LLM call{estimatedLlmCalls === 1 ? '' : 's'}
                </>
              )}
            </span>

            <button
              data-testid="refine-with-ai-button"
              onClick={handleRefineWithAI}
              disabled={selectedFilePaths.size === 0 || isRefineRunning}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                selectedFilePaths.size === 0
                  ? 'Select one or more screens to refine'
                  : isRefineRunning
                    ? 'Refine job in progress'
                    : 'Send the selected screens to the AI for name refinement'
              }
            >
              ✨ Refine selected ({selectedFilePaths.size}) with AI
            </button>
          </div>

          {/* Inline progress when a refine job is in flight or just
              completed. Auto-clears 5s after completion. */}
          {refineStatus && (
            <div
              data-testid="refine-with-ai-status"
              className={`mt-2 text-xs ${
                refineStatus.status === 'failed'
                  ? 'text-red-600 dark:text-red-400'
                  : refineStatus.status === 'completed'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-slate-600 dark:text-slate-300'
              }`}
            >
              {refineStatus.status === 'failed' && (
                <span>✗ Refine failed: {refineStatus.error ?? 'unknown error'}</span>
              )}
              {refineStatus.status === 'completed' && (
                <span>✓ Refine complete. Screen labels updated.</span>
              )}
              {isRefineRunning && (
                <span>
                  ⏳ {refineStatus.stage?.replace(/^refine:/, '') ?? 'running'}
                  {' · '}
                  {refineStatus.progress}%
                  {refineStatus.message ? ` — ${refineStatus.message}` : ''}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-auto p-4">
        {filteredItems.length === 0 && filteredModalGroups.length === 0 && filteredExportGroups.length === 0 ? (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            <p className="text-sm">No matching interactions</p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-2 text-xs text-blue-500 hover:text-blue-600"
              >
                Clear search
              </button>
            )}
          </div>
        ) : groupBy === 'none' ? (
          <div className="space-y-2">
            {/* Issue #348: Modal groups displayed first */}
            {filteredModalGroups.map((group) => (
              <ModalGroupCard
                key={`modal-${group.name}`}
                group={group}
                isExpanded={expandedModals.has(group.name)}
                onToggle={() => toggleModalExpansion(group.name)}
                onSelectInteraction={(item) => handleItemClick(item)}
              />
            ))}
            {/* Issue #349: Export groups displayed after modals */}
            {filteredExportGroups.map((group) => (
              <ExportGroupCard
                key={`export-${group.name}`}
                group={group}
                isExpanded={expandedExports.has(group.name)}
                onToggle={() => toggleExportExpansion(group.name)}
                onSelectInteraction={(item) => handleItemClick(item)}
              />
            ))}
            {/* Regular (non-modal, non-export) items */}
            {filteredItems.map((item) => (
              <InteractionCard
                key={item.flow.id}
                item={item}
                onClick={() => handleItemClick(item)}
                projectId={projectId}
                resolvedName={memoizedResolvedNames[item.flow.id]}
                aiAvailable={aiStatus?.available}
                isGenerating={generatingFlowId === item.flow.id && isGenerating}
                isDeepAnalysis={generatingFlowId === item.flow.id && isDeepAnalysisMode}
                aiSuggestion={aiSuggestions[item.flow.id]}
                onGenerateAIName={() => handleGenerateAIName(item)}
                onDeepAnalysis={() => handleDeepAnalysisName(item)}
                onSaveName={(name) => handleSaveName(item, name)}
                onAcceptAIName={() => handleAcceptAIName(item)}
                onRejectAIName={() => handleRejectAIName(item)}
                onViewContext={() => handleViewContext(item)}
                promptOverride={promptOverrides[item.flow.id]}
                enhancementSummary={enhancementSummaries[item.flow.id]}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(groupedItems)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([groupKey, items]) => {
                // A group is expanded if it's NOT in the collapsedGroups set
                const isExpanded = !collapsedGroups.has(groupKey);

                // veodiagram-issues#15 — file paths backing this group.
                // Checkbox toggles all of them. When grouping by screen,
                // these are the files that produced the screen label
                // — exactly what we'd ask the AI to refine.
                const groupFilePaths = [...new Set(items.map(i => i.file).filter((f): f is string => !!f))];
                const groupSelectedCount = groupFilePaths.filter(p => selectedFilePaths.has(p)).length;
                const groupAllSelected = groupFilePaths.length > 0 && groupSelectedCount === groupFilePaths.length;
                const groupSomeSelected = groupSelectedCount > 0 && !groupAllSelected;
                const toggleGroupSelection = () => {
                  setSelectedFilePaths(prev => {
                    const next = new Set(prev);
                    if (groupAllSelected) {
                      for (const p of groupFilePaths) next.delete(p);
                    } else {
                      for (const p of groupFilePaths) next.add(p);
                    }
                    return next;
                  });
                };

                return (
                  <div key={groupKey}>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={groupAllSelected}
                        ref={el => {
                          // Tri-state: indeterminate when partial.
                          if (el) el.indeterminate = groupSomeSelected;
                        }}
                        onChange={toggleGroupSelection}
                        disabled={groupFilePaths.length === 0 || isRefineRunning}
                        data-testid={`refine-group-checkbox-${groupKey}`}
                        title={`Select all files in "${groupKey}" for AI refine`}
                        // Stop click bubbling so the checkbox doesn't
                        // also toggle the group expansion via GroupHeader.
                        onClick={e => e.stopPropagation()}
                      />
                      <div className="flex-1">
                        <GroupHeader
                          title={groupKey}
                          count={items.length}
                          isExpanded={isExpanded}
                          onToggle={() => toggleGroup(groupKey)}
                        />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="mt-2 space-y-2 pl-2">
                        {items.map((item) => (
                          <InteractionCard
                            key={item.flow.id}
                            item={item}
                            onClick={() => handleItemClick(item)}
                            projectId={projectId}
                            resolvedName={memoizedResolvedNames[item.flow.id]}
                            aiAvailable={aiStatus?.available}
                            isGenerating={generatingFlowId === item.flow.id && isGenerating}
                            isDeepAnalysis={generatingFlowId === item.flow.id && isDeepAnalysisMode}
                            aiSuggestion={aiSuggestions[item.flow.id]}
                            onGenerateAIName={() => handleGenerateAIName(item)}
                            onDeepAnalysis={() => handleDeepAnalysisName(item)}
                            onSaveName={(name) => handleSaveName(item, name)}
                            onAcceptAIName={() => handleAcceptAIName(item)}
                            onRejectAIName={() => handleRejectAIName(item)}
                            onViewContext={() => handleViewContext(item)}
                            promptOverride={promptOverrides[item.flow.id]}
                            enhancementSummary={enhancementSummaries[item.flow.id]}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="flex flex-wrap gap-2 justify-center">
          {(['user-action', 'api-call', 'database', 'external-api'] as FlowStepType[]).map(
            (type) => {
              const colors = getStepTypeColors(type);
              return (
                <div
                  key={type}
                  className="flex items-center gap-1 text-[10px] text-slate-500"
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: colors.border }}
                  />
                  {STEP_TYPE_LABELS[type]}
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Issue #368: AI Context Preview Modal */}
      {contextPreviewItem && projectId && (
        <AIContextPreviewModal
          projectId={projectId}
          interactionId={contextPreviewItem.flow.id}
          interactionName={contextPreviewItem.flow.name}
          context={buildContextForItem(contextPreviewItem)}
          onClose={() => setContextPreviewItem(null)}
          onGenerateName={handleGenerateFromContext}
        />
      )}
    </div>
  );
}

export default InteractionCatalog;
